import { Handshake, type Role, type Session } from "../crypto/handshake";
import { SecureChannel } from "../crypto/securechannel";
import { fromBase64Url, toBase64Url } from "../crypto/util";
import { BUFFER_HIGH, BUFFER_LOW, CHUNK_SIZE } from "../config";
import {
  FRAME_CHUNK,
  FRAME_CONTROL,
  decodeControl,
  decodeFrame,
  encodeChunk,
  encodeControl,
  type Control,
  type FileOffer,
} from "./protocol";

export interface IncomingOffer {
  id: string;
  name: string;
  size: number;
  mime: string;
}

export interface Progress {
  direction: "send" | "receive";
  id: string;
  name: string;
  size: number;
  transferred: number;
}

export interface Completed {
  direction: "send" | "receive";
  id: string;
  name: string;
  size: number;
  mime: string;
  blob?: Blob;
}

export interface TransferEvents {
  /** Handshake complete; both peers proved they hold the link. */
  onReady: (sas: string) => void;
  /** A file is offered; call `decide(true|false)` to accept or decline. */
  onIncomingOffer: (
    offer: IncomingOffer,
    decide: (accept: boolean) => void
  ) => void;
  onProgress: (p: Progress) => void;
  onComplete: (c: Completed) => void;
  onError: (message: string) => void;
}

interface Inbound {
  id: string;
  name: string;
  size: number;
  mime: string;
  received: number;
  parts: Uint8Array[];
}

/**
 * Runs the PSK handshake over the data channel, then drives encrypted,
 * bidirectional file transfer. Every byte after the handshake is sealed by the
 * SecureChannel, so the network (including any TURN relay) sees only ciphertext.
 */
export class TransferSession {
  private readonly handshake: Handshake;
  private secure?: SecureChannel;
  private ready = false;
  private ephPk?: Uint8Array;
  private pendingSession?: Session;
  private inbound?: Inbound;
  private readonly decisions = new Map<string, (accepted: boolean) => void>();

  constructor(
    private readonly channel: RTCDataChannel,
    private readonly role: Role,
    psk: Uint8Array,
    private readonly events: TransferEvents
  ) {
    this.handshake = new Handshake(psk, role);
    this.channel.onmessage = (e) => void this.onMessage(e.data);
  }

  /** Start the handshake. Initiator speaks first; responder waits. */
  async begin(): Promise<void> {
    this.ephPk = await this.handshake.init();
    if (this.role === "initiator") {
      this.sendRaw({ t: "hs1", e: toBase64Url(this.ephPk) });
    }
  }

  async sendFiles(files: File[]): Promise<void> {
    for (const file of files) {
      if (!this.ready) throw new Error("secure channel not ready");
      await this.sendOne(file);
    }
  }

  close(): void {
    try {
      this.channel.close();
    } catch {
      /* noop */
    }
  }

  // ---- message routing ----

  private async onMessage(data: string | ArrayBuffer): Promise<void> {
    try {
      if (!this.ready) {
        if (typeof data === "string") await this.onHandshake(JSON.parse(data));
        return;
      }
      if (typeof data === "string") return; // unexpected after handshake
      const plain = this.secure!.open(new Uint8Array(data));
      const { type, payload } = decodeFrame(plain);
      if (type === FRAME_CONTROL) this.handleControl(decodeControl(payload));
      else if (type === FRAME_CHUNK) this.handleChunk(payload);
    } catch (err) {
      this.fail((err as Error)?.message ?? "transfer error");
    }
  }

  private async onHandshake(msg: {
    t: string;
    e?: string;
    c?: string;
  }): Promise<void> {
    if (msg.t === "hs1" && this.role === "responder") {
      const { session, confirm } = await this.handshake.derive(
        fromBase64Url(msg.e!)
      );
      this.pendingSession = session;
      this.sendRaw({
        t: "hs2",
        e: toBase64Url(this.ephPk!),
        c: toBase64Url(confirm),
      });
    } else if (msg.t === "hs2" && this.role === "initiator") {
      const { session, confirm } = await this.handshake.derive(
        fromBase64Url(msg.e!)
      );
      if (!this.handshake.verifyPeer(fromBase64Url(msg.c!))) {
        return this.fail("handshake authentication failed — possible MITM");
      }
      this.sendRaw({ t: "hs3", c: toBase64Url(confirm) });
      await this.finalize(session);
    } else if (msg.t === "hs3" && this.role === "responder") {
      if (!this.handshake.verifyPeer(fromBase64Url(msg.c!))) {
        return this.fail("handshake authentication failed — possible MITM");
      }
      await this.finalize(this.pendingSession!);
    }
  }

  private async finalize(session: Session): Promise<void> {
    this.secure = await SecureChannel.create(session.txKey, session.rxKey);
    this.ready = true;
    this.events.onReady(session.sas);
  }

  // ---- inbound ----

  private handleControl(control: Control): void {
    switch (control.t) {
      case "offer":
        this.events.onIncomingOffer(
          {
            id: control.id,
            name: control.name,
            size: control.size,
            mime: control.mime,
          },
          (accept) => this.respondToOffer(control, accept)
        );
        break;
      case "accept":
        this.resolveDecision(control.id, true);
        break;
      case "decline":
        this.resolveDecision(control.id, false);
        break;
      case "end":
        this.completeInbound(control.id);
        break;
      case "error":
        this.events.onError(control.message);
        this.inbound = undefined;
        break;
    }
  }

  private respondToOffer(offer: FileOffer, accept: boolean): void {
    if (accept) {
      this.inbound = {
        id: offer.id,
        name: offer.name,
        size: offer.size,
        mime: offer.mime,
        received: 0,
        parts: [],
      };
      this.sendControl({ t: "accept", id: offer.id });
    } else {
      this.sendControl({ t: "decline", id: offer.id });
    }
  }

  private handleChunk(payload: Uint8Array): void {
    const inbound = this.inbound;
    if (!inbound) return;
    inbound.parts.push(payload.slice()); // copy out of the shared decrypt buffer
    inbound.received += payload.length;
    if (inbound.received > inbound.size) {
      this.fail("received more data than declared");
      this.inbound = undefined;
      return;
    }
    this.events.onProgress({
      direction: "receive",
      id: inbound.id,
      name: inbound.name,
      size: inbound.size,
      transferred: inbound.received,
    });
  }

  private completeInbound(id: string): void {
    const inbound = this.inbound;
    if (!inbound || inbound.id !== id) return;
    if (inbound.received !== inbound.size) {
      // Truncated — never hand the user an incomplete file.
      this.fail("transfer ended early — file incomplete");
      this.inbound = undefined;
      return;
    }
    const blob = new Blob(inbound.parts as BlobPart[], {
      type: inbound.mime || "application/octet-stream",
    });
    this.events.onComplete({
      direction: "receive",
      id: inbound.id,
      name: inbound.name,
      size: inbound.size,
      mime: inbound.mime,
      blob,
    });
    this.inbound = undefined;
  }

  // ---- outbound ----

  private async sendOne(file: File): Promise<void> {
    const id = crypto.randomUUID();
    const accepted = new Promise<boolean>((resolve) =>
      this.decisions.set(id, resolve)
    );
    this.sendControl({
      t: "offer",
      id,
      name: file.name,
      size: file.size,
      mime: file.type,
    });
    if (!(await accepted)) return; // peer declined

    let sent = 0;
    while (sent < file.size) {
      if (this.channel.bufferedAmount > BUFFER_HIGH) await drain(this.channel);
      const chunk = new Uint8Array(
        await file.slice(sent, sent + CHUNK_SIZE).arrayBuffer()
      );
      this.sendChunk(chunk);
      sent += chunk.length;
      this.events.onProgress({
        direction: "send",
        id,
        name: file.name,
        size: file.size,
        transferred: sent,
      });
    }
    this.sendControl({ t: "end", id });
    this.events.onComplete({
      direction: "send",
      id,
      name: file.name,
      size: file.size,
      mime: file.type,
    });
  }

  // ---- low-level send ----

  private sendRaw(obj: Record<string, unknown>): void {
    this.channel.send(JSON.stringify(obj));
  }

  private sendControl(control: Control): void {
    this.channel.send(this.secure!.seal(encodeControl(control)));
  }

  private sendChunk(bytes: Uint8Array): void {
    this.channel.send(this.secure!.seal(encodeChunk(bytes)));
  }

  private resolveDecision(id: string, accepted: boolean): void {
    const resolve = this.decisions.get(id);
    if (resolve) {
      resolve(accepted);
      this.decisions.delete(id);
    }
  }

  private fail(message: string): void {
    this.events.onError(message);
    this.close();
  }
}

function drain(channel: RTCDataChannel): Promise<void> {
  return new Promise((resolve) => {
    channel.bufferedAmountLowThreshold = BUFFER_LOW;
    const handler = () => {
      channel.removeEventListener("bufferedamountlow", handler);
      resolve();
    };
    channel.addEventListener("bufferedamountlow", handler);
  });
}
